import { useState } from 'react';
import { useStoredCards, useDeleteStoredCard } from './hooks';
import { RemoveCardModal } from './removeCardModal';
import {
  CardDescription,
  CardDetails,
  CardExpiredLabel,
  CardExpiry,
  CardInfo,
  SavedCardRow,
  StyledRemoveButton,
  StyledWalletStepHeader,
  StyledWalletContainer,
  StyledSpinner,
} from './styles';
import { ConnectionErrorState } from '@/components/connectionError';
import { StyledHeaderText, CardIcon } from '@/components/checkout/uielements';
import { StyledCheckoutComponent } from '@/components/checkout/uielements/styles';
import { useSelector } from '@/redux';
import { isNetworkError } from '@/utils/networkErrors';
import { mapCardBrandToFileName } from '@/utils/payment';

export const Wallet = () => {
  const locationDetail = useSelector((state) => state.app.locations.detail);
  // Fall back to basket.location so a page reload on /account still shows Stripe cards
  // when the diner has an active basket (locations.detail isn't persisted across reloads).
  const basketLocation = useSelector((state) => state.app.basket?.location);
  const location = locationDetail ?? basketLocation;
  const stripeSavedCardsEnabled = location?.stripe_saved_cards_enabled;
  const locationId = location?.id;
  const {
    data: cards,
    isInitialLoading,
    isError,
    error,
    refetch,
  } = useStoredCards(stripeSavedCardsEnabled ? locationId : undefined);
  const deleteCard = useDeleteStoredCard();
  const [cardToRemove, setCardToRemove] = useState<{
    id: string;
    card_type: string;
    suffix: string;
  } | null>(null);

  const handleDelete = () => {
    if (!cardToRemove) return;
    deleteCard.mutate(cardToRemove.id, {
      onSettled: () => setCardToRemove(null),
    });
  };

  // A network-layer failure used to fall through to the empty-state `return null`
  // below, so the section vanished with no signal (TEAM-14780). Surface the shared
  // connection-error state with a retry instead. Non-network errors keep the prior
  // silent behavior — the copy here is connection-specific and out of their scope.
  if (isError && isNetworkError(error)) {
    return (
      <StyledCheckoutComponent>
        <StyledWalletStepHeader $enabled={true}>
          <StyledHeaderText>Saved payment methods</StyledHeaderText>
        </StyledWalletStepHeader>
        <ConnectionErrorState
          data-testid="wallet-connection-error"
          onRetry={() => void refetch()}
        />
      </StyledCheckoutComponent>
    );
  }

  if (!isInitialLoading && (!cards || cards.length === 0)) {
    return null; // Don't render the wallet section if there are no saved cards
  }

  return (
    <StyledCheckoutComponent>
      <StyledWalletStepHeader $enabled={true}>
        <StyledHeaderText>Saved payment methods</StyledHeaderText>

        {isInitialLoading && <StyledSpinner role="status" aria-label="Loading saved cards" />}
      </StyledWalletStepHeader>

      <StyledWalletContainer>
        {cards?.map((card) => {
          const expired = card.is_expired || isExpired(card.expires);
          return (
            <SavedCardRow key={card.id}>
              <CardInfo>
                <CardIcon icon={mapCardBrandToFileName(card.card_type ?? '')} type="placeholder" />
                <CardDetails>
                  <CardDescription
                    aria-label={`${card.card_type ?? 'Card'} ending in ${card.suffix ?? ''}`}
                  >
                    {card.card_type} <span aria-hidden="true">••••</span> {card.suffix}{' '}
                    {expired && <CardExpiredLabel>Expired</CardExpiredLabel>}
                  </CardDescription>
                  <CardExpiry $expired={expired}>
                    {expired ? 'Expired' : `Expires`} {formatExpiry(card.expires)}
                  </CardExpiry>
                </CardDetails>
              </CardInfo>

              {card.is_removable && (
                <StyledRemoveButton
                  aria-label={`Remove ${card.card_type ?? 'card'} ending in ${card.suffix ?? ''}`}
                  onClick={() =>
                    setCardToRemove({
                      id: card.id,
                      card_type: card.card_type ?? 'Card',
                      suffix: card.suffix ?? '****',
                    })
                  }
                >
                  Remove
                </StyledRemoveButton>
              )}
            </SavedCardRow>
          );
        })}
      </StyledWalletContainer>

      <RemoveCardModal
        isOpen={!!cardToRemove}
        cardType={cardToRemove?.card_type ?? ''}
        cardSuffix={cardToRemove?.suffix ?? ''}
        isLoading={deleteCard.isLoading}
        onKeep={() => setCardToRemove(null)}
        onRemove={handleDelete}
      />
    </StyledCheckoutComponent>
  );
};

function formatExpiry(expires: string | null | undefined): string {
  if (!expires) return '';
  // expires is in "YYYY-MM" format (e.g. "2028-10" → "10/2028")
  const [year, month] = expires.split('-');
  return month && year ? `${month}/${year}` : expires;
}

/** Client-side fallback: check if a "YYYY-MM" expiry is in the past. */
function isExpired(expires: string | null | undefined): boolean {
  if (!expires) return false;
  const [year, month] = expires.split('-');
  if (!year || !month) return false;
  const now = new Date();
  const expiryDate = new Date(Number(year), Number(month), 1); // first day after the expiry month
  return expiryDate < now;
}
